/**
 * Pure blink state for the operations-panel STACKED-TABS queue fallback
 * (control-panel spec §2). When the two protected queues can't sit side-by-side
 * without cramping, the panel shows one full-width queue at a time behind tabs.
 * The risk of a tabbed layout is that an update to the *hidden* queue is missed —
 * so the inactive tab BLINKS when its queue gets an update, and stops once the
 * operator views that tab.
 *
 * This module is just the state math (no React, no timers) so it can be unit
 * tested: a tab has an "unseen update" flag that is raised when an update lands
 * while the tab is inactive, and cleared when the operator selects the tab.
 */

export type QueueTab = 'alert' | 'assist';

export interface BlinkState {
  /** The Urgent Alert tab has an unseen update while hidden. */
  alert: boolean;
  /** The Assist tab has an unseen update while hidden. */
  assist: boolean;
}

export const NO_BLINK: BlinkState = { alert: false, assist: false };

/**
 * Record an update to `tab`. If that tab is currently hidden (not active), it
 * gains the unseen-update blink; an update to the active tab is seen live, so it
 * never blinks.
 */
export function markUpdate(state: BlinkState, tab: QueueTab, activeTab: QueueTab): BlinkState {
  if (tab === activeTab) return state;
  if (state[tab]) return state;
  return { ...state, [tab]: true };
}

/** Clear a tab's blink — called when the operator views (selects) that tab. */
export function clearTab(state: BlinkState, tab: QueueTab): BlinkState {
  if (!state[tab]) return state;
  return { ...state, [tab]: false };
}
